import { getKeyInfo } from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/utils";
import { getAffiliateMarketerIdsByShop } from "@/scenes/OrderManagement/components/OrderTable/SearchPane/AffiliateMarketerField/utils";
import { QueryOrderListParams } from "@/services/api/order";
import GlobalStore from "@/store";
import SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";
import { SupplierShopTypes } from "@/utils/ShopUtils";
import { cloneDeep } from "lodash";

interface ShopRequestParamsBuildRule {
    /** 是否启用当前规则；未启用规则时，不参与店铺过滤和参数改写 */
    shouldUse(ctx: ShopRequestParamsBuilderContext): boolean;
    /** 返回false表示当前店铺不满足此规则，需要从本次请求中剔除 */
    handle(scope: ShopRequestParamsScope, context: ShopRequestParamsBuilderContext): boolean;
    /** 所有店铺初步处理完成后，再做跨店铺的二次收敛 */
    afterBuild?(context: ShopRequestParamsBuilderContext): void;
}

interface ShopRequestParamsBuilderContext {
    shopIds: number[];
    rawParams?: QueryOrderListParams;
    shopParamsMapping: Map<number, QueryOrderListParams>;
    globalStore: typeof GlobalStore;
}

interface ShopRequestParamsScope {
    shopId: number;
    params: QueryOrderListParams;
}

class RetailerShopIdsRule implements ShopRequestParamsBuildRule {
    shouldReturnEmptyWhenNoShop = true;

    supplierPartnershipsStore: SupplierPartnershipsStore;

    retailerShopIds: number[] = [];

    constructor(supplierPartnershipsStore: SupplierPartnershipsStore) {
        this.supplierPartnershipsStore = supplierPartnershipsStore;
    }

    shouldUse(context: ShopRequestParamsBuilderContext) {
        const retailerShopIds = context.rawParams?.retailerDropShippingInfoParams?.retailerShopIds || [];

        if (!retailerShopIds.length || !this.supplierPartnershipsStore) {
            return false;
        }

        this.retailerShopIds = retailerShopIds;

        return true;
    }

    handle(scope: ShopRequestParamsScope, context: ShopRequestParamsBuilderContext) {
        const shop = context.globalStore.shopMapping?.get(scope.shopId);

        if (!this.supplierPartnershipsStore || !SupplierShopTypes.includes(shop?.type)) {
            return false;
        }

        // 多店铺页选择的是代理商，接口请求需要转换成当前供应商店铺可见的代理商范围。
        const partnerships = this.supplierPartnershipsStore.supplierPartnershipsMap.get(scope.shopId) || [];
        const retailerShopIdSet = new Set(this.retailerShopIds);
        const matchedRetailerShopIds = partnerships
            .map((partnership) => partnership.shopId)
            .filter((partnershipShopId) => {
                return typeof partnershipShopId === "number" && retailerShopIdSet.has(partnershipShopId);
            });

        if (!matchedRetailerShopIds.length) {
            // 没有交集时剔除此店铺，避免回退成普通查询。
            return false;
        }

        scope.params.retailerDropShippingInfoParams = {
            retailerShopIds: matchedRetailerShopIds,
        };

        return true;
    }
}

class LinkedProductFieldRule implements ShopRequestParamsBuildRule {
    private skuPropertiesFieldSkuKeys: string[];
    private titleFieldSkuKeys: string[];
    private externalIdFieldSkuKeys: string[];
    private hasSkuPropertiesSkuInfoIds = false;
    private hasExternalIdSkuInfoIds = false;

    shouldUse(context: ShopRequestParamsBuilderContext) {
        const rawParams = (context.rawParams || {}) as any;

        // 联动字段保存的是带店铺信息的skuKey，后面会按店铺拆成接口需要的skuInfoIds。
        this.skuPropertiesFieldSkuKeys = (rawParams.skuPropertiesFieldSkuKeys as unknown) as string[];
        this.titleFieldSkuKeys = (rawParams.titleFieldSkuKeys as unknown) as string[];
        this.externalIdFieldSkuKeys = (rawParams.externalIdFieldSkuKeys as unknown) as string[];

        return !!(
            this.skuPropertiesFieldSkuKeys?.length ||
            this.titleFieldSkuKeys?.length ||
            this.externalIdFieldSkuKeys?.length
        );
    }

    handle(scope: ShopRequestParamsScope) {
        const shopParams = scope.params;

        // skuKey只是前端中间态，实际请求只保留按店铺匹配出的skuInfoIds。
        delete shopParams["skuPropertiesFieldSkuKeys"];
        delete shopParams["titleFieldSkuKeys"];
        delete shopParams["externalIdFieldSkuKeys"];

        let hasMatchedLinkField = false;

        if (this.skuPropertiesFieldSkuKeys) {
            shopParams.skuPropertiesSkuInfoIds = [];

            // 只取当前店铺命中的skuInfoId，避免A店铺带着B店铺的商品条件去查。
            scope.params.skuPropertiesSkuInfoIds = this.collectMatchedSkuInfoIds(
                this.skuPropertiesFieldSkuKeys,
                scope.shopId,
            );

            if (scope.params.skuPropertiesSkuInfoIds.length) {
                this.hasSkuPropertiesSkuInfoIds = true;
                hasMatchedLinkField = true;
            }
        }

        if (this.titleFieldSkuKeys) {
            shopParams.titleSkuInfoIds = [];

            // 标题联动同样按店铺拆分；是否保留由本轮所有联动字段的命中结果共同决定。
            scope.params.titleSkuInfoIds = this.collectMatchedSkuInfoIds(this.titleFieldSkuKeys, scope.shopId);

            if (scope.params.titleSkuInfoIds.length) {
                hasMatchedLinkField = true;
            }
        }

        if (this.externalIdFieldSkuKeys) {
            shopParams.externalIdSkuInfoIds = [];

            // 商家编码联动按店铺拆分，并记录是否存在任意店铺命中。
            scope.params.externalIdSkuInfoIds = this.collectMatchedSkuInfoIds(
                this.externalIdFieldSkuKeys,
                scope.shopId,
            );

            if (scope.params.externalIdSkuInfoIds.length) {
                this.hasExternalIdSkuInfoIds = true;
                hasMatchedLinkField = true;
            }
        }

        return hasMatchedLinkField;
    }

    afterBuild(context: ShopRequestParamsBuilderContext) {
        /**
         * 过滤掉不需要查询的店铺
         * 如果任一店铺选择了sku属性或者external属性，需要将无选择这两个属性的店铺删除掉
         * 举例：
         * 如果A店铺有商品P1，B店铺有商品P2，P1有属性P1S，P2有属性P2S，
         * 当用户在商品标题组件中同时选择了P1和P2，这个时候再去属性下拉中选择P1S
         * 这个时候可以确认P2商品是用户不需要的，不需要把P2的订单给搜索出来
         */
        context.shopParamsMapping.forEach((shopParams, shopId) => {
            if (!shopParams) {
                return;
            }

            if (this.hasSkuPropertiesSkuInfoIds || this.hasExternalIdSkuInfoIds) {
                if (!shopParams.skuPropertiesSkuInfoIds?.length && !shopParams.externalIdSkuInfoIds?.length) {
                    console.log("delete shopParams:", shopId);

                    context.shopParamsMapping.delete(shopId);
                }
            }
        });

        /**
         * 过滤掉不需要查询的店铺
         * 如果店铺同时选择了sku属性和external属性，需要将无【并选择】的进行删除
         * 举例：
         * 如果A店铺有属性【男士】【女士】，编码有【1036】，B店铺有属性【女士】，编码有【1030】和【1036】
         * 当用户在商品属性组件中同时选择了【男士】和【女士】，此时编码组件会显示【1030】和【1036】，这个时候再去编码组件下拉中选择【1030】时，
         * 这个时候可以由于A店铺的没有【1030】的编码，为了避免A店铺直接使用属性的搜索字段进行搜索而产生并集，需要给A店铺的编码skuInfoId设置为一个无意义且无法查到值的数组
         */
        context.shopParamsMapping.forEach((shopParams) => {
            if (!shopParams) {
                return;
            }

            if (this.skuPropertiesFieldSkuKeys?.length && this.externalIdFieldSkuKeys?.length) {
                if (!shopParams.skuPropertiesSkuInfoIds?.length) {
                    shopParams.skuPropertiesSkuInfoIds = [-1];
                }

                if (!shopParams.externalIdSkuInfoIds?.length) {
                    shopParams.externalIdSkuInfoIds = [-1];
                }
            }
        });
    }

    private collectMatchedSkuInfoIds(fieldSkuKeys: string[], shopId: number) {
        // skuKey格式中包含店铺ID；这里只留下属于当前店铺的skuId部分。
        const skuInfoIds: number[] = [];

        fieldSkuKeys.forEach((key) => {
            const [keyShopId, skuId] = getKeyInfo(key);

            if (keyShopId !== shopId) {
                return;
            }

            const skuIdNum = Number(skuId);

            if (!Number.isNaN(skuIdNum)) {
                skuInfoIds.push(skuIdNum);
            }
        });

        return skuInfoIds;
    }
}

class AffiliateMarketerFieldRule implements ShopRequestParamsBuildRule {
    private affiliateMarketerKeys: string[];

    shouldUse(context: ShopRequestParamsBuilderContext) {
        const rawParams = (context.rawParams || {}) as any;

        this.affiliateMarketerKeys = rawParams.affiliateMarketerKeys;

        return !!this.affiliateMarketerKeys?.length;
    }

    handle(scope: ShopRequestParamsScope) {
        delete scope.params["affiliateMarketerKeys"];

        const affiliateMarketerIds = getAffiliateMarketerIdsByShop(this.affiliateMarketerKeys, scope.shopId);

        if (!affiliateMarketerIds.length) {
            return false;
        }

        scope.params.affiliateMarketerIds = affiliateMarketerIds;

        return true;
    }
}

class ShopRequestParamsBuilder {
    rules: ShopRequestParamsBuildRule[] = [];
    context: ShopRequestParamsBuilderContext;

    constructor(shopIds: number[], globalStore: typeof GlobalStore, rawParams: QueryOrderListParams) {
        this.context = {
            shopIds,
            rawParams: cloneDeep(rawParams),
            globalStore,
            shopParamsMapping: new Map<number, QueryOrderListParams>(),
        };
    }

    addRule(rule: ShopRequestParamsBuildRule) {
        this.rules.push(rule);
        return this;
    }

    build() {
        // 只有搜索参数里真正带了跨店铺特殊条件时，才启用按店铺拆分逻辑。
        const enabledRules = this.rules.filter((rule) => rule.shouldUse(this.context));

        if (!enabledRules?.length) {
            // 返回undefined表示调用方可以直接使用原始queryParams，不需要店铺级参数映射。
            return undefined;
        }

        this.context.shopIds.forEach((shopId) => {
            const scope: ShopRequestParamsScope = {
                shopId,
                // 每个店铺独立复制一份参数，规则只改当前店铺的请求体。
                params: cloneDeep(this.context.rawParams || {}) as QueryOrderListParams,
            };

            let shouldKeepShop = true;

            enabledRules.forEach((rule) => {
                // 任一规则不满足时，该店铺从本次查询/导出范围里剔除。
                if (rule.handle(scope, this.context) === false) {
                    shouldKeepShop = false;
                }
            });

            console.log(`shopId=${shopId},shouldKeepShop=${shouldKeepShop}`);

            if (shouldKeepShop) {
                this.context.shopParamsMapping.set(shopId, scope.params);
            }
        });

        // 初筛后再执行规则的全局收敛，例如联动字段组合下的交集过滤。
        enabledRules.forEach((rule) => {
            rule.afterBuild?.(this.context);
        });

        console.log("this.context:", this.context);

        // 空Map是有效业务结果：表示特殊条件已启用，但没有任何店铺命中，调用方应展示0条或阻止导出。
        // if (!this.context.shopParamsMapping.size) return undefined;
        return this.context.shopParamsMapping;
    }
}

interface ShopRequestParamsProps {
    globalStore: typeof GlobalStore;
    shopIds: number[];
    rawParams: QueryOrderListParams;
    supplierPartnershipsStore: SupplierPartnershipsStore;
}

/**
 * 根据店铺ID和原始参数，构建每个店铺的请求参数映射
 * 目前这套规则仅用于跨店铺页面和导出功能
 * 联动/代理商筛选后没有任何店铺命中，就应该显示 0 条，不要退回普通查询(即不用请求后端接口了)
 * 说明：
 * 1. 当使用联动搜索时，仅需请求包含选中商品的店铺
 * 2. 根据商品在下拉框中的选择情况，过滤掉未选择相关属性的店铺请求
 *
 * eg:
 *  现在店铺下拉中有id为1和2的店铺，但是商品下拉中仅选择了1店铺的商品，这时候，不需要去请求店铺2
 *
 * @returns 返回Map结构的店铺请求参数映射表（key为店铺ID），
 * 1. 若无任何按店铺拆分的特殊参数，则返回undefined，业务可直接使用queryParams进行请求
 * 2. 若代理商等强过滤条件存在，但最终没有任何有效店铺，则返回空Map
 */
export function getShopRequestParamsMapping(props: ShopRequestParamsProps) {
    const { globalStore, shopIds, rawParams, supplierPartnershipsStore } = props;

    // 规则之间是“且”的关系：商品联动和代理商过滤都满足的店铺才会保留。
    return new ShopRequestParamsBuilder(shopIds, globalStore, rawParams)
        .addRule(new LinkedProductFieldRule())
        .addRule(new AffiliateMarketerFieldRule())
        .addRule(new RetailerShopIdsRule(supplierPartnershipsStore))
        .build();
}
